import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  const { clear } = useCart();
  const { user } = useAuth();
  const sid = params.get("session_id");

  useEffect(() => {
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); clear(); return; }
        if (data.status === "expired") { setState("expired"); return; }
      } catch { /* retry */ }
      if (++tries >= 15) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    if (sid) poll(); else setState("timeout");
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && <><Loader2 className="w-10 h-10 animate-spin text-[#0D9488] mx-auto" /><h1 className="font-display text-3xl mt-6">Confirming your payment…</h1></>}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-[#0D9488] mx-auto" />
          <h1 data-testid="payment-success-title" className="font-display text-4xl mt-6">Thank you for your order</h1>
          <p className="text-slate-600 mt-4">A confirmation email is on its way. While your order ships, build your personalized Wave Therapy plan.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/questionnaire" data-testid="payment-build-plan-btn" className="rounded-full bg-[#0D9488] text-white px-6 py-3 font-semibold">Build my plan</Link>
            {user ? <Link to="/dashboard" data-testid="payment-dashboard-btn" className="rounded-full border border-[#E4DFD4] bg-white px-6 py-3 font-semibold">View orders</Link> : <span data-testid="payment-guest-receipt-note" className="px-2 py-3 text-sm text-slate-600">Your receipt is on its way to your email.</span>}
          </div>
        </>
      )}
      {(state === "expired" || state === "timeout") && (
        <>
          <XCircle className="w-12 h-12 text-amber-600 mx-auto" />
          <h1 className="font-display text-3xl mt-6" data-testid="payment-pending-title">We couldn't confirm your payment yet</h1>
          <p className="text-slate-600 mt-3">If you completed checkout, your order will appear in your dashboard shortly.</p>
          <Link to="/shop" className="inline-block mt-6 text-[#0D9488] font-semibold">Back to shop</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-cancel-page">
      <h1 className="font-display text-4xl">Checkout cancelled</h1>
      <p className="text-slate-600 mt-4">No payment was taken. Your cart is saved.</p>
      <Link to="/shop" data-testid="payment-cancel-shop-btn" className="inline-block mt-8 rounded-full bg-[#0B1013] text-white px-6 py-3 font-semibold">Continue shopping</Link>
    </div>
  );
}
